import React, { useState, useEffect, useCallback } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import InquiryForm from './components/InquiryForm';
import SpecialOffers from './components/SpecialOffers';
import FeaturedSection from './components/FeaturedSection';
import PricingSection from './components/PricingSection';
import ServicesSection from './components/ServicesSection';
import AboutSection from './components/AboutSection';
import ContactSection from './components/ContactSection';
import CallbackSection from './components/CallbackSection';
import Footer from './components/Footer';
import UmrahEnquiryForm from './components/UmrahEnquiryForm';
import SearchResults from './components/SearchResults';
import PackageSelection from './components/PackageSelection';
import TransportZiyarat from './components/TransportZiyarat';
import Checkout from './components/Checkout';
import LeadsCRM from './components/LeadsCRM';
import PackagesDisplay from './components/PackagesDisplay';
import PrivacyPolicy from './components/PrivacyPolicy';
import { CurrencyProvider } from './context/CurrencyContext';
import './App.css';

// URL state keys
const STATE_KEYS = {
  VIEW: 'view',
  SEARCH: 'search',
  PACKAGE: 'package'
};

function App() {
  const [builderOpen, setBuilderOpen] = useState(false);
  const [currentView, setCurrentView] = useState('home');
  const [searchParams, setSearchParams] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Check if we're on the admin/CRM route or privacy page
  useEffect(() => {
    const path = window.location.pathname;
    if (path === '/admin' || path === '/admin/' || path === '/crm' || path === '/crm/') {
      setCurrentView('admin');
    } else if (path === '/privacy' || path === '/privacy/' || path === '/privacy-policy' || path === '/privacy-policy/') {
      setCurrentView('privacy');
    }
  }, []);

  // Load state from URL on mount and handle browser navigation
  useEffect(() => {
    const loadStateFromURL = () => {
      const path = window.location.pathname;
      if (path === '/admin' || path === '/admin/' || path === '/crm' || path === '/crm/') {
        setCurrentView('admin');
        return;
      }
      if (path === '/privacy' || path === '/privacy/' || path === '/privacy-policy' || path === '/privacy-policy/') {
        setCurrentView('privacy');
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const view = urlParams.get(STATE_KEYS.VIEW) || 'home';
      
      // Load search params from sessionStorage if available
      const storedSearch = sessionStorage.getItem('umrahSearchParams');
      const storedPackage = sessionStorage.getItem('umrahSelectedPackage');
      
      if (storedSearch) {
        try {
          setSearchParams(JSON.parse(storedSearch));
        } catch (e) {
          console.error('Error parsing stored search params:', e);
        }
      }
      
      if (storedPackage) {
        try {
          setSelectedPackage(JSON.parse(storedPackage));
        } catch (e) {
          console.error('Error parsing stored package:', e);
        }
      }
      
      // Set view based on URL and available data
      if (view === 'package-selection' && storedSearch) {
        setCurrentView('package-selection');
      } else if (view === 'transport-ziyarat' && storedSearch && storedPackage) {
        setCurrentView('transport-ziyarat');
      } else if (view === 'custom-builder' && storedSearch) {
        setCurrentView('custom-builder');
      } else if (view === 'checkout' && storedPackage) {
        setCurrentView('checkout');
      } else {
        setCurrentView('home');
      }
    };

    loadStateFromURL();
    
    // Handle browser back/forward
    const handlePopState = () => {
      loadStateFromURL();
    };
    
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Update URL when view changes
  const updateURL = useCallback((view) => {
    const url = new URL(window.location);
    if (view === 'home') {
      url.searchParams.delete(STATE_KEYS.VIEW);
    } else {
      url.searchParams.set(STATE_KEYS.VIEW, view);
    }
    window.history.pushState({}, '', url);
  }, []);

  const handleSearch = async (params) => {
    console.log('Search triggered with params:', params);
    setSearchParams(params);
    sessionStorage.setItem('umrahSearchParams', JSON.stringify(params));
    setCurrentView('package-selection');
    updateURL('package-selection');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectPackage = (packageData) => {
    console.log('Package selected:', packageData);
    setSelectedPackage(packageData);
    sessionStorage.setItem('umrahSelectedPackage', JSON.stringify(packageData));
    setCurrentView('transport-ziyarat');
    updateURL('transport-ziyarat');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleContinueToCheckout = (finalPackage) => {
    console.log('Final package:', finalPackage);
    setSelectedPackage(finalPackage);
    sessionStorage.setItem('umrahSelectedPackage', JSON.stringify(finalPackage));
    setCurrentView('checkout');
    updateURL('checkout');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCustomize = () => {
    setCurrentView('custom-builder');
    updateURL('custom-builder');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBackToHome = () => {
    setCurrentView('home');
    setSearchParams(null);
    setSelectedPackage(null);
    sessionStorage.removeItem('umrahSearchParams');
    sessionStorage.removeItem('umrahSelectedPackage');
    updateURL('home');
  };

  const handleBackToPackages = () => {
    setCurrentView('package-selection');
    setSelectedPackage(null);
    sessionStorage.removeItem('umrahSelectedPackage');
    updateURL('package-selection');
  };

  const handleBackToSearch = () => {
    setCurrentView('custom-builder');
    setSelectedPackage(null);
    sessionStorage.removeItem('umrahSelectedPackage');
    updateURL('custom-builder');
  };

  // Render admin/CRM view
  if (currentView === 'admin') {
    return <LeadsCRM />;
  }

  // Render privacy policy page
  if (currentView === 'privacy') {
    return <PrivacyPolicy />;
  }

  // Render package selection view (NEW - after search)
  if (currentView === 'package-selection' && searchParams) {
    return (
      <CurrencyProvider>
        <div className="App">
          <Header onOpenBuilder={() => setBuilderOpen(true)} />
          <PackageSelection 
            searchParams={searchParams}
            onSelectPackage={handleSelectPackage}
            onCustomize={handleCustomize}
            onBack={handleBackToHome}
          />
        </div>
      </CurrencyProvider>
    );
  }

  // Render Transport & Ziyarat view (after package selection)
  if (currentView === 'transport-ziyarat' && selectedPackage && searchParams) {
    return (
      <CurrencyProvider>
        <div className="App">
          <TransportZiyarat 
            searchParams={searchParams}
            selectedPackageData={selectedPackage}
            onContinue={handleContinueToCheckout}
            onBack={handleBackToPackages}
          />
        </div>
      </CurrencyProvider>
    );
  }

  // Render custom builder view (full search results)
  if (currentView === 'custom-builder' && searchParams) {
    return (
      <CurrencyProvider>
        <div className="App">
          <SearchResults 
            searchParams={searchParams}
            onSelectPackage={handleSelectPackage}
            onBack={handleBackToPackages}
          />
        </div>
      </CurrencyProvider>
    );
  }

  // Render checkout view
  if (currentView === 'checkout' && selectedPackage) {
    return (
      <CurrencyProvider>
        <div className="App">
          <Checkout
            searchParams={searchParams}
            selectedPackage={selectedPackage}
            onBack={() => {
              setCurrentView('transport-ziyarat');
              updateURL('transport-ziyarat');
            }}
            onComplete={() => {
              // Clear state after successful booking
              setSearchParams(null);
              setSelectedPackage(null);
              sessionStorage.removeItem('umrahSearchParams');
              sessionStorage.removeItem('umrahSelectedPackage');
              setCurrentView('home');
              updateURL('home');
            }}
          />
        </div>
      </CurrencyProvider>
    );
  }

  // Default home view
  return (
    <CurrencyProvider>
      <div className="App">
        <Header onOpenBuilder={() => setBuilderOpen(true)} />
        <Hero onOpenBuilder={() => setBuilderOpen(true)} onSearch={handleSearch} />
        <InquiryForm onOpenBuilder={() => setBuilderOpen(true)} />
        <PackagesDisplay onEnquire={(pkg) => {
          // Scroll to enquiry form when "Book Now" is clicked
          const enquiryForm = document.querySelector('#enquiry-form');
          if (enquiryForm) {
            enquiryForm.scrollIntoView({ behavior: 'smooth' });
          }
        }} />
        <SpecialOffers onOpenBuilder={() => setBuilderOpen(true)} />
        <FeaturedSection onOpenBuilder={() => setBuilderOpen(true)} />
        <ServicesSection onOpenBuilder={() => setBuilderOpen(true)} />
        <AboutSection onOpenBuilder={() => setBuilderOpen(true)} />
        <ContactSection />
        <CallbackSection />
        <Footer onOpenBuilder={() => setBuilderOpen(true)} />
      </div>
    </CurrencyProvider>
  );
}

export default App;
